import { useState } from 'react'
import { X } from 'lucide-react'
import { Button, Field, IconButton, Input, Select } from '@/components/common'
import { collectionSportLabel } from './collectionModel'
import styles from './collectionEditor.module.css'

export function ReferencePicker({ label, values = [], choices = [], disabled = false, max = 20, placeholder, onChange }) {
  const byKey = new Map(choices.map((choice) => [`${choice.sport}:${choice.id}`, choice]))
  const selectedKeys = new Set(values.map((v) => `${v.sport}:${v.id}`))
  return (
    <div className={styles.picker}>
      <Field label={label}>
        <Select
          size="compact"
          value=""
          disabled={disabled || values.length >= max}
          onChange={(event) => {
            if (event.target.value) {
              const choice = byKey.get(event.target.value)
              if (choice) {
                onChange([...values, { sport: choice.sport, id: choice.id, name: choice.name }])
              }
            }
          }}
        >
          <option value="">{values.length ? 'Add another' : (placeholder || `Any ${label.toLowerCase()}`)}</option>
          {choices.filter((choice) => !selectedKeys.has(`${choice.sport}:${choice.id}`)).map((choice) => (
            <option key={`${choice.sport}:${choice.id}`} value={`${choice.sport}:${choice.id}`}>
              {choice.name} ({collectionSportLabel(choice.sport)})
            </option>
          ))}
        </Select>
      </Field>
      {values.length ? (
        <ul className={styles.chips}>
          {values.map((val) => {
            const key = `${val.sport}:${val.id}`
            const name = val.name || byKey.get(key)?.name || val.id
            return (
              <li key={key}>
                <span>{name}</span>
                <IconButton
                  label={`Remove ${name}`}
                  disabled={disabled}
                  onClick={() => onChange(values.filter((item) => `${item.sport}:${item.id}` !== key))}
                >
                  <X size={12} />
                </IconButton>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

export function AudiencesInput({ audiences = [], disabled = false, max = 8, label = 'Audience IDs', format = 'string', onChange }) {
  const [draft, setDraft] = useState('')
  const isObjectFormat = format === 'ims'

  const add = (e) => {
    e.preventDefault()
    const trimmed = draft.trim()
    if (!trimmed || audiences.length >= max) return
    if (isObjectFormat) {
      if (audiences.some((a) => (typeof a === 'string' ? a : a.id) === trimmed)) return
      onChange([...audiences, { provider: 'ims', id: trimmed }])
    } else {
      if (audiences.includes(trimmed)) return
      onChange([...audiences, trimmed])
    }
    setDraft('')
  }

  const remove = (target) => {
    if (isObjectFormat) {
      const next = audiences.filter((a) => (typeof a === 'string' ? a : a.id) !== target)
      onChange(next.length ? next : undefined)
    } else {
      onChange(audiences.filter((item) => item !== target))
    }
  }

  const hasDuplicate = isObjectFormat
    ? audiences.some((a) => (typeof a === 'string' ? a : a.id) === draft.trim())
    : audiences.includes(draft.trim())

  return (
    <div className={styles.audiences}>
      <form onSubmit={add} className={styles.audienceForm}>
        <Field label={label}>
          <Input
            size="compact"
            value={draft}
            disabled={disabled || audiences.length >= max}
            placeholder="Audience ID (e.g. nj-vip)"
            onChange={(e) => setDraft(e.target.value)}
          />
        </Field>
        <Button
          type="submit"
          size="compact"
          disabled={disabled || !draft.trim() || audiences.length >= max || hasDuplicate}
        >
          Add
        </Button>
      </form>
      {audiences.length ? (
        <ul className={styles.chips}>
          {audiences.map((item) => {
            const id = typeof item === 'string' ? item : item.id
            const display = isObjectFormat ? `ims:${id}` : id
            return (
              <li key={id}>
                <span>{display}</span>
                <IconButton
                  label={`Remove audience ${id}`}
                  disabled={disabled}
                  onClick={() => remove(id)}
                >
                  <X size={12} />
                </IconButton>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
